import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearAuthMessages } from '../slices/authSlice';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import FormInput from './FormInput';

// Reusable sign-in form used in SignIn page
const SignInForm = forwardRef(function SignInForm({ onAuthenticated } = {}, ref) {
  const dispatch = useDispatch();
  const errorMessage = useSelector((state) => state.auth.errorMessage);
  const loading = useSelector((state) => state.auth.loading);

  // Key used for remembering email
  const REMEMBER_EMAIL_KEY = 'rememberEmail';

  // Read saved email synchronously so initial render can avoid focusing email when remember is active
  let initialSavedEmail = '';
  try {
    initialSavedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
  } catch (e) {
    initialSavedEmail = '';
  }

  const [email, setEmail] = useState(initialSavedEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(!!initialSavedEmail);

  // Ref for password input to focus after restoring email
  const passwordInputRef = useRef(null);

  // Expose imperative focus method so parent can call it when refresh fails
  useImperativeHandle(ref, () => ({
    focusPassword: () => {
      setPassword('');
      try {
        passwordInputRef.current?.focus();
      } catch (e) {}
    }
  }));

  // On mount, if remember is active focus the password input
  useEffect(() => {
    if (initialSavedEmail) {
      try {
        // Use setTimeout to ensure the input is mounted and focusable
        setTimeout(() => {
          passwordInputRef.current?.focus();
        }, 0);
      } catch (e) {}
    }
  }, [initialSavedEmail]);

  const handleSignIn = (e) => {
    e.preventDefault();
    dispatch(clearAuthMessages());

    dispatch(login({ email, password, rememberMe }))
      .unwrap()
      .then(() => {
        // Persist or clear the remembered email according to checkbox
        try {
          if (rememberMe) {
            if (email) localStorage.setItem(REMEMBER_EMAIL_KEY, email);
          } else {
            localStorage.removeItem(REMEMBER_EMAIL_KEY);
          }
        } catch (err) {
          // ignore storage errors
        }

        if (typeof onAuthenticated === 'function') onAuthenticated();
      })
      .catch(() => {
        // error handled by Redux
      });
  };

  return (
    <form onSubmit={handleSignIn} className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-gray-300">
          Email address
        </label>
        <FormInput
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus={!rememberMe}
          variant="glass"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-gray-300">
          Password
        </label>
        <div className="relative">
          <FormInput
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            variant="glass"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pr-10"
            ref={passwordInputRef}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5 text-gray-300" aria-hidden="true" />
            ) : (
              <EyeIcon className="h-5 w-5 text-gray-300" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {errorMessage && (
        <p role="alert" aria-live="assertive" className="text-red-600 dark:text-red-400 text-sm">
          {errorMessage}
        </p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            id="remember-me"
            name="remember-me"
            type="checkbox"
            className="h-4 w-4 rounded border border-slate-300 dark:border-gray-600 text-teal-700 focus:ring-teal-600 bg-surface dark:bg-gray-870"
            checked={rememberMe}
            onChange={() => setRememberMe(!rememberMe)}
          />
          <label htmlFor="remember-me" className="ml-3 block text-sm text-slate-700 dark:text-gray-300">
            Remember me
          </label>
        </div>

        <div className="text-sm">
          <a href="/forgot-password" className="font-medium text-teal-700 dark:text-teal-400 hover:underline">Forgot password?</a>
        </div>
      </div>

      <div>
        <button
          type="submit"
          className="flex w-full justify-center items-center rounded-md bg-slate-900 text-white dark:bg-white dark:text-gray-900 px-4 py-2 text-sm font-medium h-10 shadow hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white"
          disabled={loading}
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5 text-white dark:text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291a7.978 7.978 0 01-1.664-1.415A8.018 8.018 0 012.34 14H.12A9.985 9.985 0 006 19.29V17.29z"></path>
            </svg>
          ) : (
            'Sign in'
          )}
        </button>
      </div>
    </form>
  );
});

export default SignInForm;
